import { constructRoutes } from 'single-spa-layout';

/* eslint-disable import/no-relative-packages */
import {
  getMicroFrontendsByMountPoint,
  MicroFrontend,
  MicroFrontendMountPoint,
  MicroFrontendRouteDefinition,
  MondrianInstance,
} from '../../../config';
import { ErrorType } from '../types/errors';
import { generateErrorHTML } from './errorHandler';
import { loaders } from './loaders';

/**
 * Render the JSON layout definition for the given micro-frontend for
 * single-spas layout engine.
 * @param mfe
 */
export const renderMicroFrontend = (mfe: MicroFrontend) => {
  const application = {
    type: 'application',
    name: mfe.packageName,
    loader: loaders[mfe.loader],
    // errors are only rendered at the mount point of the individual
    // micro-frontend when it is mounted at the content mount point.
    // micro-frontends that are rendered at the navigation or core
    // mount-point are considered part of the platform and are essential
    // for the platform to function. therefore, errors for those are rendered
    // at higher level.
    error:
      mfe.mountPoint === MicroFrontendMountPoint.CONTENT
        ? generateErrorHTML(ErrorType.LOAD)
        : false,
  };

  const routes = (Array.isArray(mfe.routes) ? mfe.routes : [mfe.routes]).map(
    (route: MicroFrontendRouteDefinition) => ({
      type: 'route',
      path: route.path,
      exact: route.exact ?? false,
      routes: mfe.mountAlways ? [] : [application],
    })
  );

  return mfe.mountAlways ? [application, ...routes] : routes;
};

/**
 * Render the JSON layout definition for all micro-frontends of the given
 * instance that should be mounted at the given mount point.
 * @param instance
 * @param mountPoint
 */
export const renderMountPoint = (
  instance: MondrianInstance,
  mountPoint: MicroFrontendMountPoint
) => {
  return getMicroFrontendsByMountPoint(instance, mountPoint)
    .map((microFrontend) => renderMicroFrontend(microFrontend))
    .flat();
};

/**
 * Render the redirect JSON layout definition for the given instance.
 * @param instance
 */
export const renderRedirects = (instance: MondrianInstance) => {
  return Object.fromEntries(
    (instance.redirects || []).map((redirect) => [redirect.from, redirect.to])
  );
};

/**
 * Render the JSON layout definition for the given instance.
 * @param instance
 */
export const renderJSONLayout = (instance: MondrianInstance) => {
  return {
    redirects: renderRedirects(instance),
    routes: [
      {
        type: 'div',
        attrs: [
          {
            name: 'class',
            value: 'h-screen w-full hidden',
          },
          {
            name: 'id',
            value: 'platform-error',
          },
        ],
      },
      {
        type: 'single-spa-router',
        attrs: [
          {
            name: 'id',
            value: 'platform-container',
          },
        ],
        routes: [
          ...renderMountPoint(instance, MicroFrontendMountPoint.CORE),
          ...renderMountPoint(instance, MicroFrontendMountPoint.GLOBAL),
          {
            type: 'div',
            attrs: [
              {
                name: 'class',
                value:
                  'flex w-full h-screen absolute top-0 left-0 overflow-hidden',
              },
            ],
            routes: [
              {
                type: 'nav',
                attrs: [
                  {
                    name: 'class',
                    value: 'sticky top-0 z-10',
                  },
                ],
                routes: renderMountPoint(
                  instance,
                  MicroFrontendMountPoint.NAVIGATION
                ),
              },
              {
                type: 'div',
                attrs: [
                  {
                    name: 'class',
                    value:
                      'w-full z-0 overflow-x-hidden mx-auto mb-0 h-full [&>div]:h-full',
                  },
                ],
                routes: [
                  ...renderMountPoint(
                    instance,
                    MicroFrontendMountPoint.CONTENT
                  ),
                  {
                    type: 'route',
                    default: true,
                    routes: [
                      {
                        type: 'application',
                        name: '@iu/myiu.fe.app.404',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
};

/**
 * Construct the single-spa routes for the given instance.
 * @param instance
 */
export const constructSingleSpaRoutes = (instance: MondrianInstance) => {
  return constructRoutes(renderJSONLayout(instance));
};
